import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell, AlertTriangle, AlertOctagon, CheckCircle2, RefreshCw, Mail,
  Clock, ExternalLink, BellOff, ShieldAlert, BookX, UserX, BatteryLow,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../hooks/use-toast';
import api from '../../api';
import { cn } from '../../lib/utils';

const SEVERITY_META = {
  critical: { color: 'rose',  Icon: AlertOctagon,   label: 'Critique' },
  high:     { color: 'amber', Icon: AlertTriangle,  label: 'Important' },
  medium:   { color: 'sky',   Icon: Bell,           label: 'À surveiller' },
  low:      { color: 'emerald', Icon: CheckCircle2, label: 'Faible' },
};

const TYPE_ICON = {
  theme_crisis: ShieldAlert,
  explosive_question: BookX,
  at_risk_student: UserX,
  low_engagement: BatteryLow,
};

const TYPE_LABEL = {
  theme_crisis: 'Thème en difficulté',
  explosive_question: 'Question explosive',
  at_risk_student: 'Élève à risque',
  low_engagement: 'Engagement faible',
};

function AlertCard({ alert, onDismiss, onSnooze }) {
  const meta = SEVERITY_META[alert.severity] || SEVERITY_META.medium;
  const TypeIcon = TYPE_ICON[alert.type] || Bell;
  const colorBg = {
    rose: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    sky: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
  }[meta.color];
  const colorBadge = {
    rose: 'bg-rose-600 text-white',
    amber: 'bg-amber-500 text-white',
    sky: 'bg-sky-600 text-white',
    emerald: 'bg-emerald-600 text-white',
  }[meta.color];
  const colorIcon = {
    rose: 'text-rose-600',
    amber: 'text-amber-600',
    sky: 'text-sky-600',
    emerald: 'text-emerald-600',
  }[meta.color];

  return (
    <div
      className={cn('rounded-2xl border p-4 sm:p-5 transition-shadow hover:shadow-md', colorBg)}
      data-testid={`alert-${alert.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn('w-11 h-11 rounded-xl bg-white dark:bg-black/20 flex items-center justify-center shrink-0 ring-1 ring-border', colorIcon)}>
          <TypeIcon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={cn('text-[10px] uppercase tracking-wider font-black px-2 py-0.5 rounded-full', colorBadge)}>
              <meta.Icon className="w-3 h-3 inline -mt-0.5 mr-1" />
              {meta.label}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              {TYPE_LABEL[alert.type] || alert.type}
            </span>
          </div>
          <h3 className="text-sm sm:text-base font-bold leading-snug">{alert.title}</h3>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed">{alert.description}</p>
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 font-bold text-foreground">
              <span className="text-lg leading-none">{alert.metric}</span>
              <span className="text-muted-foreground font-normal">{alert.metric_label}</span>
            </span>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {alert.action_url ? (
              <Link to={alert.action_url} className="inline-flex">
                <Button size="sm" variant="default" className="rounded-xl text-xs h-8" data-testid={`alert-${alert.id}-action`}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {alert.action_label || 'Voir'}
                </Button>
              </Link>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl text-xs h-8"
              onClick={() => onSnooze(alert.id, 24)}
              data-testid={`alert-${alert.id}-snooze`}
            >
              <Clock className="w-3.5 h-3.5 mr-1.5" />
              Reporter 24 h
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-xl text-xs h-8 text-muted-foreground hover:text-foreground"
              onClick={() => onDismiss(alert.id)}
              data-testid={`alert-${alert.id}-dismiss`}
            >
              <BellOff className="w-3.5 h-3.5 mr-1.5" />
              Écarter
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, count, color }) {
  return (
    <div className={cn('rounded-xl px-3 py-2 flex items-center gap-2', color)} data-testid={`pill-${label.toLowerCase()}`}>
      <span className="text-2xl font-black tabular-nums">{count}</span>
      <span className="text-xs font-bold uppercase tracking-wider opacity-90">{label}</span>
    </div>
  );
}

export default function AdminAlerts() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ total: 0, by_severity: {}, by_type: {}, alerts: [] });
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const d = await api.getAdminAlerts({ refresh: refresh ? 1 : 0 });
      setData(d);
    } catch (e) {
      toast({ title: 'Impossible de charger les alertes', description: 'Vérifiez votre connexion.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismiss = async (id) => {
    await api.dismissAdminAlert(id, 24 * 30); // 30 jours = quasi définitif
    toast({ title: 'Alerte écartée', description: 'Elle ne réapparaîtra pas pendant 30 jours.' });
    setData((d) => ({ ...d, alerts: d.alerts.filter((a) => a.id !== id), total: Math.max(0, d.total - 1) }));
  };

  const handleSnooze = async (id, hours = 24) => {
    await api.dismissAdminAlert(id, hours);
    toast({ title: 'Alerte reportée', description: `Réapparaîtra dans ${hours} heures.` });
    setData((d) => ({ ...d, alerts: d.alerts.filter((a) => a.id !== id), total: Math.max(0, d.total - 1) }));
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api.sendAdminAlertsDigest();
      if (res.sent) {
        toast({ title: 'Digest envoyé', description: `${res.count} alertes envoyées par email.` });
      } else {
        toast({ title: 'Aucun email envoyé', description: res.reason || 'Resend non configuré.' });
      }
    } catch (e) {
      toast({ title: 'Erreur d\'envoi', description: 'Impossible d\'envoyer l\'email.', variant: 'destructive' });
    } finally {
      setSendingDigest(false);
    }
  };

  const sev = data.by_severity || {};
  const groupedByType = (data.alerts || []).reduce((acc, a) => {
    (acc[a.type] = acc[a.type] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="admin-alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200 px-3 py-1 text-xs font-bold mb-3">
            <Bell className="w-3.5 h-3.5" /> CENTRE D'ALERTES
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Alertes pédagogiques</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Détection automatique de signaux faibles : thèmes en difficulté, questions explosives, élèves à risque,
            engagement faible. Calculé toutes les 30 minutes (cache).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={() => load({ refresh: true })}
            disabled={refreshing || loading}
            data-testid="alerts-refresh"
          >
            <RefreshCw className={cn('w-4 h-4 mr-1.5', refreshing && 'animate-spin')} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={handleSendDigest}
            disabled={sendingDigest || data.total === 0}
            data-testid="alerts-send-digest"
          >
            <Mail className={cn('w-4 h-4 mr-1.5', sendingDigest && 'animate-pulse')} />
            Email récap
          </Button>
        </div>
      </div>

      {/* Pills résumé */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="alerts-summary">
        <StatPill label="Critiques" count={sev.critical || 0} color="bg-rose-100 dark:bg-rose-900/30 text-rose-900 dark:text-rose-200" />
        <StatPill label="Importantes" count={sev.high || 0} color="bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200" />
        <StatPill label="À surveiller" count={sev.medium || 0} color="bg-sky-100 dark:bg-sky-900/30 text-sky-900 dark:text-sky-200" />
        <StatPill label="Total actives" count={data.total} color="bg-muted text-foreground" />
      </div>

      {/* Liste des alertes */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
      ) : data.alerts.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10 p-8 sm:p-12 text-center" data-testid="alerts-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
          <h2 className="text-xl font-bold text-emerald-900 dark:text-emerald-200">Tout va bien 🎉</h2>
          <p className="text-sm text-emerald-800/80 dark:text-emerald-300/80 mt-2 max-w-md mx-auto">
            Aucun signal faible détecté sur la plateforme. Le système surveille en continu :
            taux de réussite par thème, questions ratées, élèves nouveaux, engagement.
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            Les seuils de détection sont conservatifs (n_élèves ≥ 1, n_erreurs ≥ 10) pour éviter les fausses alertes.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="alerts-list">
          {Object.entries(groupedByType).map(([type, alerts]) => (
            <section key={type} className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                {React.createElement(TYPE_ICON[type] || Bell, { className: 'w-4 h-4' })}
                {TYPE_LABEL[type] || type}
                <span className="text-xs font-normal">({alerts.length})</span>
              </h2>
              <div className="space-y-3">
                {alerts.map((a) => (
                  <AlertCard key={a.id} alert={a} onDismiss={handleDismiss} onSnooze={handleSnooze} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Footer info */}
      <div className="rounded-xl bg-muted p-4 text-xs text-muted-foreground" data-testid="alerts-footer">
        <p className="leading-relaxed">
          <strong>Comment ça marche ?</strong> Les détecteurs analysent toutes les 30 min les données de la plateforme :
          erreurs élèves (collection <code>user_progress</code>), navigation (<code>page_views</code>), inscriptions (<code>users</code>).
          Les alertes écartées sont stockées en DB et masquées le temps choisi.
          Quand l'email Resend est configuré, vous pouvez aussi recevoir un récap.
        </p>
      </div>
    </div>
  );
}
